import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { OnlineIndicator } from "./OnlineIndicator";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import type { Conversation } from "@/hooks/useTeamComms";
import { FolderOpen, Users, MessageSquare } from "lucide-react";

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  currentUserId: string;
  isOnline: (userId: string) => boolean;
}

export const ConversationList = ({ conversations, activeId, onSelect, currentUserId, isOnline }: ConversationListProps) => {
  const getConvoName = (c: Conversation) => {
    if (c.title) return c.title;
    const others = c.members.filter(m => m.user_id !== currentUserId);
    if (others.length === 0) return "You";
    return others.map(m => `${m.first_name} ${m.last_name}`).join(", ");
  };

  const getConvoAvatar = (c: Conversation) => {
    const others = c.members.filter(m => m.user_id !== currentUserId);
    return others[0] ?? null;
  };

  const getTypeIcon = (type: string) => {
    if (type === "project") return <FolderOpen className="h-3 w-3 text-muted-foreground" />;
    if (type === "group") return <Users className="h-3 w-3 text-muted-foreground" />;
    return null;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 && (
          <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
            <MessageSquare className="h-10 w-10 mb-2 opacity-40" />
            <p className="text-sm">No conversations yet</p>
          </div>
        )}
        {conversations.map((c) => {
          const avatarUser = getConvoAvatar(c);
          const otherOnline = c.members.some(m => m.user_id !== currentUserId && isOnline(m.user_id));
          return (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b border-border/50",
                activeId === c.id
                  ? "bg-primary/10"
                  : "hover:bg-accent/50"
              )}
            >
              <div className="relative shrink-0">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={avatarUser?.profile_photo_url ?? undefined} />
                  <AvatarFallback className="text-xs bg-muted">
                    {avatarUser ? `${avatarUser.first_name[0]}${avatarUser.last_name[0]}` : "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-0.5 -right-0.5">
                  <OnlineIndicator isOnline={otherOnline} size="sm" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {getTypeIcon(c.type)}
                  <span className="font-medium text-sm truncate">{getConvoName(c)}</span>
                </div>
                {c.last_message && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {c.last_message.body}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                {c.last_message && (
                  <span className="text-[10px] text-muted-foreground">
                    {formatDistanceToNow(new Date(c.last_message.created_at), { addSuffix: false })}
                  </span>
                )}
                {c.unread_count > 0 && (
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0 min-w-[18px] flex items-center justify-center">
                    {c.unread_count}
                  </Badge>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
